/**
 * 통합 자격증 매핑 시스템
 * 점진적 마이그레이션 전략: 기존 18개는 보호, 신규 확장만 공식 영문명 사용
 * 
 * 우선순위:
 * 1. PROTECTED_LEGACY - 기존 CBT 보유 자격증 (절대 변경 금지)
 * 2. OFFICIAL_ENGLISH - Q-Net 공식 영문명 기반
 * 3. DYNAMIC_GENERATION - 자동 생성 + 충돌 해결
 */

// 1순위: 절대 보호되는 기존 CBT 자격증들 (URL 변경 금지)
export const PROTECTED_LEGACY_MAPPINGS = {
  // 전기 관련
  'electrical-engineer': {
    korean: '전기기사',
    english: 'Engineer Electricity', // Q-Net 공식
    urlSlug: 'electrical-engineer', // 기존 URL 유지
    qnetCode: '1150',
    hasCBT: true,
    protected: true
  },
  'electrical-industrial-engineer': {
    korean: '전기산업기사',
    english: 'Industrial Engineer Electricity',
    urlSlug: 'electrical-industrial-engineer',
    qnetCode: '2140',
    hasCBT: true,
    protected: true
  },
  'electrical-technician': {
    korean: '전기기능사',
    english: 'Craftsman Electricity',
    urlSlug: 'electrical-technician',
    qnetCode: '7780',
    hasCBT: true,
    protected: true
  },
  'electrical-construction-engineer': {
    korean: '전기공사기사',
    english: 'Engineer Electrical Construction', // 추정
    urlSlug: 'electrical-construction-engineer',
    qnetCode: '1160',
    hasCBT: true,
    protected: true
  },

  // IT 관련
  'information-processing-engineer': {
    korean: '정보처리기사',
    english: 'Engineer Information Processing',
    urlSlug: 'information-processing-engineer',
    qnetCode: '1320',
    hasCBT: true,
    protected: true
  },
  'computer-applications-level1': {
    korean: '컴퓨터활용능력1급',
    english: 'Computer Specialist in Spreadsheet & Database Level-Ⅰ',
    urlSlug: 'computer-applications-level1',
    qnetCode: '2670',
    hasCBT: true,
    protected: true
  },
  'computer-applications-level2': {
    korean: '컴퓨터활용능력2급',
    english: 'Computer Specialist in Spreadsheet & Database Level-Ⅱ',
    urlSlug: 'computer-applications-level2',
    qnetCode: '2671',
    hasCBT: true,
    protected: true
  },

  // 건축/안전 관련
  'architectural-engineer': {
    korean: '건축기사',
    english: 'Engineer Architecture',
    urlSlug: 'architectural-engineer',
    qnetCode: '1630',
    hasCBT: true,
    protected: true
  },
  'industrial-safety-engineer': {
    korean: '산업안전기사',
    english: 'Engineer Industrial Safety',
    urlSlug: 'industrial-safety-engineer',
    qnetCode: '1431',
    hasCBT: true,
    protected: true
  },
  'industrial-hygiene-engineer': {
    korean: '산업위생관리기사',
    english: 'Engineer Industrial Hygiene Management',
    urlSlug: 'industrial-hygiene-engineer',
    qnetCode: '1441',
    hasCBT: true,
    protected: true
  },

  // 소방
  'fire-engineer-mechanic': {
    korean: '소방설비기사(기계분야)',
    english: 'Engineer Fire Protection System - Mechanical',
    urlSlug: 'fire-engineer-mechanic',
    qnetCode: '1900',
    hasCBT: true,
    protected: true
  },
  'fire-protection-engineer-electrical': {
    korean: '소방설비기사(전기분야)',
    english: 'Engineer Fire Protection System - Electrical',
    urlSlug: 'fire-protection-engineer-electrical',
    qnetCode: '1910',
    hasCBT: true,
    protected: true
  },

  // 기계/운전
  'hvac-refrigeration-industrial-engineer': {
    korean: '공조냉동기계산업기사',
    english: 'Industrial Engineer Air-Conditioning and Refrigerating Machinery',
    urlSlug: 'hvac-refrigeration-industrial-engineer',
    qnetCode: '2590',
    hasCBT: true,
    protected: true
  },
  'forklift-operator-technician': {
    korean: '지게차운전기능사',
    english: 'Craftsman Fork Lift Truck Operator',
    urlSlug: 'forklift-operator-technician',
    qnetCode: '7875',
    hasCBT: true,
    protected: true
  },
  'excavator-operator': {
    korean: '굴착기운전기능사',
    english: 'Craftsman Excavating Machine Operator',
    urlSlug: 'excavator-operator',
    qnetCode: '7862',
    hasCBT: true,
    protected: true
  },

  // 기타
  'hazardous-materials-industrial-engineer': {
    korean: '위험물산업기사',
    english: 'Industrial Engineer Hazardous material',
    urlSlug: 'hazardous-materials-industrial-engineer',
    qnetCode: '2121',
    hasCBT: true,
    protected: true
  },
  'forest-technician': {
    korean: '산림기능사',
    english: 'Craftsman Forest',
    urlSlug: 'forest-technician',
    qnetCode: '7632',
    hasCBT: true,
    protected: true
  },
  'korean-cooking-technician': {
    korean: '한식조리기능사',
    english: 'Craftsman Korean Cooking', // 추정 (CSV에 없음)
    urlSlug: 'korean-cooking-technician',
    qnetCode: '7910',
    hasCBT: true,
    protected: true
  }
};

// 공식 영문명 → URL 슬러그 변환 규칙
export const ENGLISH_TO_SLUG_RULES = {
  // 직급별 변환
  'Engineer': 'engineer',
  'Industrial Engineer': 'industrial-engineer', 
  'Craftsman': 'technician',
  'Specialist': 'specialist',
  
  // 분야별 변환
  'Electricity': 'electrical',
  'Architecture': 'architectural',
  'Information Processing': 'information-processing',
  'Industrial Safety': 'industrial-safety',
  'Industrial Hygiene Management': 'industrial-hygiene-management',
  'Fire Protection System': 'fire-protection-system',
  'Air-Conditioning and Refrigerating Machinery': 'hvac-refrigeration-machinery',
  'Fork Lift Truck Operator': 'forklift-operator',
  'Excavating Machine Operator': 'excavator-operator',
  'Hazardous material': 'hazardous-materials',
  'Forest': 'forest',
  
  // 레벨 변환
  'Level-Ⅰ': 'level1',
  'Level-Ⅱ': 'level2',
  'Level-Ⅲ': 'level3'
};

/**
 * 공식 영문명을 URL 안전한 슬러그로 변환
 * @param {string} englishName - 공식 영문명
 * @returns {string} - URL 슬러그
 */
export function convertEnglishToSlug(englishName) {
  if (!englishName) return '';
  
  let slug = englishName.toLowerCase();
  
  // 규칙 기반 변환
  Object.entries(ENGLISH_TO_SLUG_RULES).forEach(([english, replacement]) => {
    const regex = new RegExp(english.toLowerCase(), 'gi');
    slug = slug.replace(regex, replacement);
  });
  
  // 일반적인 정리
  slug = slug
    .replace(/[()]/g, '') // 괄호 제거
    .replace(/[-\s]+/g, '-') // 공백을 하이픈으로
    .replace(/[^a-z0-9-]/g, '') // 영숫자와 하이픈만 유지
    .replace(/^-+|-+$/g, '') // 앞뒤 하이픈 제거
    .replace(/-+/g, '-'); // 중복 하이픈 제거
  
  return slug;
}

/**
 * 충돌 해결 - 기존 슬러그와 중복되지 않도록 보장
 * @param {string} baseSlug - 기본 슬러그
 * @param {Set} existingSlugs - 기존 슬러그들
 * @returns {string} - 충돌 해결된 슬러그
 */
export function resolveSlugConflict(baseSlug, existingSlugs = new Set()) {
  if (!existingSlugs.has(baseSlug)) {
    return baseSlug;
  }
  
  // 기존 보호된 슬러그와 충돌하는 경우 번호 suffix 추가
  let counter = 2;
  let newSlug = `${baseSlug}-${counter}`;
  
  while (existingSlugs.has(newSlug)) {
    counter++;
    newSlug = `${baseSlug}-${counter}`;
  }
  
  return newSlug;
}

/**
 * 새로운 자격증 매핑 생성 (공식 영문명 기반)
 * @param {string} koreanName - 한글 자격증명
 * @param {string} englishName - 공식 영문명
 * @param {string} qnetCode - Q-Net 코드
 * @returns {Object} - 매핑 객체
 */
export function createNewCertificationMapping(koreanName, englishName, qnetCode) {
  const baseSlug = convertEnglishToSlug(englishName);
  const protectedSlugs = new Set(Object.keys(PROTECTED_LEGACY_MAPPINGS));
  const finalSlug = resolveSlugConflict(baseSlug, protectedSlugs);
  
  return {
    korean: koreanName,
    english: englishName,
    urlSlug: finalSlug,
    qnetCode: qnetCode,
    hasCBT: false,
    protected: false,
    source: 'official-english'
  };
}

/**
 * 매핑 우선순위에 따른 자격증 정보 조회
 * @param {string} identifier - 한글명, 영문명, 또는 슬러그
 * @returns {Object|null} - 자격증 정보
 */
export function getCertificationInfo(identifier) {
  // 1순위: 보호된 레거시 매핑에서 찾기
  for (const [slug, info] of Object.entries(PROTECTED_LEGACY_MAPPINGS)) {
    if (slug === identifier || 
        info.korean === identifier || 
        info.english === identifier) {
      return { ...info, urlSlug: slug };
    }
  }
  
  // 2순위: API에서 동적으로 생성된 매핑 (향후 구현)
  // TODO: API 기반 매핑 시스템 연동
  
  return null;
}

/**
 * 모든 보호된 슬러그 목록 반환
 * @returns {Array<string>} - 보호된 슬러그 배열
 */
export function getProtectedSlugs() {
  return Object.keys(PROTECTED_LEGACY_MAPPINGS);
}

/**
 * 한글명을 슬러그로 변환 (역방향 매핑)
 * @param {string} koreanName - 한글 자격증명
 * @returns {string|null} - 슬러그 또는 null
 */
export function getSlugFromKorean(koreanName) {
  for (const [slug, info] of Object.entries(PROTECTED_LEGACY_MAPPINGS)) {
    if (info.korean === koreanName) {
      return slug;
    }
  }
  return null;
}

// 기본 export
const unifiedMappings = {
  PROTECTED_LEGACY_MAPPINGS,
  ENGLISH_TO_SLUG_RULES,
  convertEnglishToSlug,
  resolveSlugConflict,
  createNewCertificationMapping,
  getCertificationInfo,
  getProtectedSlugs,
  getSlugFromKorean
};

export default unifiedMappings;